import { Map } from "immutable"
import {
    measurerClient,
    turbineClient,
    metaAIClient,
    metaAIAppClient,
    ApolloClient
} from "neql"
import { useEffect, useMemo, useRef } from "react"
import { fakeApolloClient, NeqlClientMap } from "../Context/NeqlClientContext"
import { getApiKey } from "../Helpers/Functions/Selectors/auth"
import { useAppState } from "./useReduxState"

export function useNeqlClients(): NeqlClientMap {
    const metaAIAppClientsRef = useRef(
        Map<string, ApolloClient<any> | undefined>()
    )
    const apiKey = useAppState(getApiKey)
    const graphqlClients = useMemo(() => {
        if (!apiKey) {
            return {
                turbine: fakeApolloClient,
                measurer: fakeApolloClient,
                metaAI: fakeApolloClient,
                metaAIApp: () => fakeApolloClient
            }
        }

        return {
            turbine: turbineClient(apiKey) ?? fakeApolloClient,
            measurer: measurerClient(apiKey) ?? fakeApolloClient,
            metaAI: metaAIClient(apiKey) ?? fakeApolloClient,
            metaAIApp: (appId: string) => {
                const clients = metaAIAppClientsRef.current

                if (clients.has(appId)) {
                    return clients.get(appId) ?? fakeApolloClient
                }

                const newClient = metaAIAppClient(apiKey, appId)

                metaAIAppClientsRef.current = clients.set(appId, newClient)

                return newClient ?? fakeApolloClient
            }
        }
    }, [apiKey])

    useEffect(() => {
        return () => {
            graphqlClients.turbine?.clearStore()
            graphqlClients.measurer?.clearStore()
            graphqlClients.metaAI?.clearStore()
        }
    }, [
        graphqlClients.measurer,
        graphqlClients.turbine,
        apiKey,
        graphqlClients.metaAI
    ])

    useEffect(() => {
        return () => {
            metaAIAppClientsRef.current.forEach(client => {
                client?.clearStore()
            })
            metaAIAppClientsRef.current =
                Map<string, ApolloClient<any> | undefined>()
        }
    }, [apiKey])

    return graphqlClients
}
